import React from 'react';
import { Plus, X } from 'lucide-react';

import {
  type SortLevel,
  MAX_SORT_LEVELS,
} from '../../utils/sortLevels';

/** A column a reviewer may sort by: the backend field, and the label they see. */
export interface SortableColumn {
  field: string;
  label: string;
}

interface SortDialogProps {
  levels: SortLevel[];
  columns: SortableColumn[];
  onChange: (levels: SortLevel[]) => void;
}

/**
 * Spreadsheet-style sort panel: "Sort by <column>", then "Then by <column>".
 *
 * This is also the keyboard-accessible route to a multi-level sort — shift-click
 * on a header has no keyboard equivalent, so without this panel the feature
 * would be mouse-only.
 *
 * Changes apply immediately rather than behind an OK button, matching the
 * column picker beside it and the rest of the grid.
 *
 * Decision: PDR-055 — see docs/decisions/PDR-055-validation-queue-sorts-on-ordered-levels.md
 */
export const SortDialog: React.FC<SortDialogProps> = ({ levels, columns, onChange }) => {
  const usedFields = new Set(levels.map((l) => l.field));
  const firstUnused = columns.find((c) => !usedFields.has(c.field));
  const atCap = levels.length >= MAX_SORT_LEVELS;

  const setField = (index: number, field: string) =>
    onChange(levels.map((l, i) => (i === index ? { ...l, field } : l)));

  const setDirection = (index: number, desc: boolean) =>
    onChange(levels.map((l, i) => (i === index ? { ...l, desc } : l)));

  const removeLevel = (index: number) =>
    onChange(levels.filter((_, i) => i !== index));

  const addLevel = () => {
    if (!firstUnused || atCap) return;
    onChange([...levels, { field: firstUnused.field, desc: false }]);
  };

  return (
    <div
      role="dialog"
      aria-label="Sort rows"
      data-testid="sort-dialog"
      className="absolute left-0 top-full mt-1 z-30 w-[26rem] bg-bg-surface rounded-lg border border-border shadow-lg p-3"
    >
      <p className="pb-2 mb-2 text-xs font-semibold text-text-tertiary uppercase tracking-wide border-b border-border">
        Sort rows
      </p>

      {levels.length === 0 && (
        <p className="py-2 text-sm text-text-tertiary">
          No sorting applied. Add a level to choose how rows are ordered.
        </p>
      )}

      {levels.map((level, index) => (
        <div key={`${level.field}-${index}`} className="flex items-center gap-2 mb-2">
          <span
            data-testid={`sort-level-label-${index}`}
            className="w-16 shrink-0 text-xs text-text-tertiary"
          >
            {index === 0 ? 'Sort by' : 'Then by'}
          </span>

          <select
            data-testid={`sort-field-${index}`}
            aria-label={index === 0 ? 'Sort by column' : `Then by column ${index + 1}`}
            value={level.field}
            onChange={(e) => setField(index, e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 text-sm rounded border border-border bg-bg-surface text-text-primary"
          >
            {columns
              // A column may appear once. Its own level keeps its option so the
              // select has a value to show.
              .filter((c) => c.field === level.field || !usedFields.has(c.field))
              .map((c) => (
                <option key={c.field} value={c.field}>
                  {c.label}
                </option>
              ))}
          </select>

          <select
            data-testid={`sort-direction-${index}`}
            aria-label={`Direction for level ${index + 1}`}
            value={level.desc ? 'desc' : 'asc'}
            onChange={(e) => setDirection(index, e.target.value === 'desc')}
            className="w-32 shrink-0 px-2 py-1 text-sm rounded border border-border bg-bg-surface text-text-primary"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>

          <button
            type="button"
            onClick={() => removeLevel(index)}
            data-testid={`sort-remove-level-${index}`}
            aria-label={`Remove sort level ${index + 1}`}
            className="shrink-0 p-1 rounded text-text-tertiary hover:text-qc-error hover:bg-bg-subtle transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addLevel}
        disabled={atCap || !firstUnused}
        data-testid="sort-add-level"
        className="mt-1 inline-flex items-center gap-1.5 px-2 py-1 text-sm font-medium rounded text-interactive-primary hover:bg-interactive-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" />
        Add another level
      </button>

      {atCap && (
        <p className="mt-1 text-xs text-text-tertiary">
          Up to {MAX_SORT_LEVELS} levels.
        </p>
      )}
    </div>
  );
};
